import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAdminLogout, useAdminLogin as useLogin } from '@/hooks/api/useAdminAuth';
import useApiErrorHandling from '@/hooks/util/useApiErrorHandler';
import useValidation from '@/hooks/util/useValidation';
import { useCenterValidationSchema } from '@/pages/CenterValidation/schema/CenterValidationSchema';
import { AdminLoginResponse, GenericApiResponse } from '@/services/api';
import { ErrorCode, getErrorMessageFromCode } from '@/utils/errorMessages';

interface LoginParameters {
  password: string;
}

type ErrorsState = Partial<LoginParameters>;

export interface AdminDetails {
  centerCode: string;
  centerName: string;
  programCode: string;
  adminCode: string;
  isFln?: boolean;
}

const getAdminDetails = (): AdminDetails | null => {
  const storedAdminDetails = localStorage.getItem('adminDetails');
  // eslint-disable-next-line unicorn/no-null
  return storedAdminDetails ? (JSON.parse(storedAdminDetails) as AdminDetails) : null;
};

const mapCodeToFieldErrors = (code: ErrorCode, t: (key: string) => string) => {
  const errors: ErrorsState = {};
  switch (code as ErrorCode) {
    case ErrorCode.IncorrectPassword: {
      errors.password = getErrorMessageFromCode(code, t);
      break;
    }
    case ErrorCode.InvalidPassword: {
      errors.password = getErrorMessageFromCode(code, t);
      break;
    }
    default:
  }
  return errors;
};

const getErrors = (errorStates: ErrorsState[]) => {
  return errorStates.find((errorState) =>
    Object.keys(errorState).some((key) => errorState[key as keyof ErrorsState]),
  );
};

const useAdminLogin = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { apiErrors, handleApiError, clearApiError } = useApiErrorHandling((code) =>
    mapCodeToFieldErrors(code, t),
  );

  const schema = useCenterValidationSchema();
  const {
    errors: validationErrors,
    validate,
    clearError,
  } = useValidation({ password: schema.password });

  const resetValidation = useCallback(
    (type?: string) => {
      if (type) clearError(type);
      if (type) clearApiError(type);
    },
    [clearApiError, clearError],
  );

  const mutateOptions = {
    onMutate: () => setIsSubmitting(true),
    onError: () => setIsSubmitting(false),
    onSuccess: (data: GenericApiResponse<AdminLoginResponse>) => {
      if (data.data.code) {
        handleApiError(data.data.code);
      }
      setIsSubmitting(false);
    },
  };
  const { mutate: login } = useLogin(mutateOptions);
  const { mutate: logout } = useAdminLogout();

  const onAdminLogin = useCallback(
    (password: string, onSuccess?: (data: GenericApiResponse<AdminLoginResponse>) => void) => {
      const adminId = localStorage.getItem('adminId');
      if (adminId && validate({ password })) {
        login({ password, adminId }, { onSuccess });
      }
    },
    [validate, login],
  );

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  return {
    onAdminLogin,
    errors: getErrors([validationErrors, apiErrors]),
    isSubmitting,
    handleLogout,
    resetValidation,
    adminDetails: getAdminDetails(),
  };
};

export default useAdminLogin;
